import {Badge, Box, Text, Title} from '@mantine/core';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {FaAngleDown, FaAngleUp} from 'react-icons/fa6';
import {MdOutlineShare} from 'react-icons/md';
import {REPORT_ANALYTICS_HEAD_IMAGE} from '../../../../config/constants/img';
import {COMPANIES} from '../../../../config/constants/routes';
import type {ReportSummaryType} from '../../../../core/entities/reportAnalytics/schema';
import {useAuth} from '../../../../core/usecases/auth/AuthContext';
import {ShareButton} from '../../../../shared/components/ShareButton';
import {buildCompanyFollowGtmContent} from '../../../../shared/services/buildCompanyFollowGtmContent';
import {useLineClamp} from '../../usecase/useLineClamp';
import {useTextOverflow} from '../../usecase/useTextOverflow';
import {CustomFollowCompanyButton} from './CustomFollowCompanyButton';
import {TransitionFollowButton} from './TransitionFollowButton';

type Props = {
  reportSummaryData?: ReportSummaryType;
  currentUrl: string;
  isPC: boolean;
  reportType: string;
};

export const ResultHeadBox: React.FC<Props> = ({
  reportSummaryData,
  currentUrl,
  isPC,
  reportType,
}) => {
  const {status} = useAuth();
  const companyID = reportSummaryData?.companyID;
  const [opened, handleMoreClick] = useLineClamp();
  const logoUrl = reportSummaryData?.logoURLs['140x105'];
  const isTextOverflow = useTextOverflow(
    reportSummaryData?.description ?? '',
    isPC ? 110 : 35,
  );
  const companyName = reportSummaryData?.companyName;
  const departmentName = reportSummaryData?.departmentName;
  const indsutryName = reportSummaryData?.industryName;
  const description = reportSummaryData?.description;
  const isIntern = reportType === 'intern';
  const initialIsFollowed = reportSummaryData?.followed ?? false;
  const initialFollowCount = reportSummaryData?.followCount ?? 0;

  const [isFollowed, setIsFollowed] = useState<boolean>(initialIsFollowed);
  const [followCount, setFollowCount] = useState<number>(initialFollowCount);

  const handleFollowChange = useCallback(
    (newIsFollowed: boolean, newFollowCount: number) => {
      setIsFollowed(newIsFollowed);
      setFollowCount(newFollowCount);
    },
    [],
  );

  const [showTransitionButton, setShowTransitionButton] = useState(false);
  const followButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (followButtonRef.current) {
        const {bottom} = followButtonRef.current.getBoundingClientRect();
        setShowTransitionButton(bottom <= 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Box
      className="w-full flex items-center justify-center pc:p-24 p-12 gap-8"
      style={{
        backgroundImage: `url(${REPORT_ANALYTICS_HEAD_IMAGE})`,
        backgroundSize: 'cover',
      }}
    >
      <Box className="w-full flex flex-col pc:p-8">
        <Title order={1} className="sr-only">
          {`AI分析結果 ${isIntern ? 'インターン' : '本選考'} ${companyName} / ${departmentName}`}
        </Title>
        <Box className="flex justify-between">
          <Box className="flex items-center gap-16">
            <Text className="text-white text-2xl">AI分析結果</Text>
            <Badge
              variant="outline"
              size="lg"
              className="text-white border-white  h-[80%]"
              style={{borderWidth: 2}}
            >
              {isIntern ? 'インターン' : '本選考'}
            </Badge>
          </Box>
          {isPC && (
            <Box className="flex justify-end">
              <ShareButton
                url={currentUrl}
                title={`${companyName} / ${departmentName} ${isIntern ? 'インターン' : '本選考'} AI分析結果`}
                leftIcon={<MdOutlineShare />}
                variant="outline"
                className="text-white border-white border-2 text-right"
                radius="xl"
              >
                結果をシェアする
              </ShareButton>
            </Box>
          )}
        </Box>
        <Box className="flex text-md text-white my-12">
          <Text>
            {companyName} / {departmentName}
          </Text>
        </Box>
        <Box className="flex bg-black bg-opacity-20 rounded-12 p-8 mb-12 text-white">
          {logoUrl !== '' && (
            <Box className="flex w-[100px] p-4">
              <Box className="w-full">
                <img
                  src={logoUrl}
                  alt="Company Logo"
                  className="bg-white object-contain w-full cursor-pointer rounded-4 p-8"
                  onClick={() => {
                    window.open(`${COMPANIES}/${String(companyID)}`, '_blank');
                  }}
                />
              </Box>
            </Box>
          )}
          <Box className="flex-col w-full pc:p-12 p-8">
            <Box className="flex pc:mb-4 mb-8 justify-between">
              <Box>
                <Box className="flex items-center pc:gap-8 flex-wrap">
                  <Text className="text-white text-justify">{companyName}</Text>
                  <Text className="text-sm">{indsutryName}</Text>
                  {companyID && (
                    <Box
                      className="ml-4"
                      style={{minWidth: '120px'}}
                      ref={followButtonRef}
                    >
                      <CustomFollowCompanyButton
                        companyId={companyID}
                        isFollowed={isFollowed}
                        followCount={followCount}
                        onFollowChange={handleFollowChange}
                        dataGtmContent={buildCompanyFollowGtmContent({
                          companyId: companyID,
                          companyName: companyName ?? '',
                          location: 'report-analytics',
                        })}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              {isTextOverflow && (
                <Box className="">
                  <Text
                    className="pc:text-base text-sm text-right cursor-pointer hover:underline focus:outline-none whitespace-nowrap"
                    onClick={handleMoreClick}
                  >
                    {opened ? <FaAngleUp /> : <FaAngleDown />}
                  </Text>
                </Box>
              )}
            </Box>
            <Box
              className={`text-sm overflow-hidden text-justify ${opened ? 'line-clamp-none' : 'line-clamp-2'}`}
            >
              {description}
            </Box>
          </Box>
        </Box>
        {!isPC && (
          <Box className="flex justify-center">
            <ShareButton
              url={currentUrl}
              title={`${companyName} / ${departmentName} ${isIntern ? 'インターン' : '本選考'} AI分析結果`}
            />
          </Box>
        )}
        {companyID && status === 'authenticated' && (
          <TransitionFollowButton
            companyId={companyID}
            isFollowed={isFollowed}
            followCount={followCount}
            mounted={showTransitionButton}
            transition="slide-up"
            duration={500}
            timingFunction="ease"
            onFollowChange={handleFollowChange}
            customFollowText="この企業をフォローする"
            dataGtmContent={buildCompanyFollowGtmContent({
              companyId: companyID,
              companyName: companyName ?? '',
              location: 'report-analytics-float',
            })}
          />
        )}
      </Box>
    </Box>
  );
};
